<template>
  <div class="sustainability-page">
    <Permissions package="sustainability" />

    <div v-if="showSustainability">
      <CarbonFlowSteppers :step="1" />

      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
      </div>

      <div class="page-wrapper">

        <div class="toolbar">

          <div class="tab-switcher">
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'daycent' }"
              @click="activeTab = 'daycent'; isToggleDaycentOn = false"
            >
              Daycent
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'mems' }"
              @click="activeTab = 'mems'; isToggleDaycentOn = true"
            >
              Mems
            </button>
            <button
              class="tab-btn"
              :class="{ active: activeTab === 'enteric' }"
              @click="activeTab = 'enteric'"
            >
              Enteric Methane
            </button>
          </div>

          <div class="toolbar-actions">
            <!-- Carbon price -->
            <button class="action-btn carbon-price-btn" @click="handleAdjustCarbonCreditModalClick">
              Carbon Price:&nbsp;
              <span class="price-value">${{ carbonCreditDollarValue }}/Tonne</span>
              <span class="info-icon" title="CO2e use the users $/Tonne input to calculate the carbon credit dollar value. By default, we will use $20/Tonne.">ⓘ</span>
            </button>
            <button class="action-btn" @click="loadPastures">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              Load Data
            </button>

            <button v-if="!isReadOnly" class="action-btn action-btn--dark" @click="downloadFieldSelection">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download
            </button>
          </div>
        </div>

        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input
                    type="checkbox"
                    class="custom-checkbox"
                    ref="fieldSelectAllCheckbox"
                    @change="selectAllCheckBox"
                  />
                </th>
                <th class="col-action">
                  <button
                    v-if="isModalOpen && checkedCount > 0"
                    class="run-model-btn"
                    :disabled="isReadOnly"
                    @click="runModelSetTypeModalOpen = true"
                  >
                    Run Model
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </th>
                <th>Pasture ID</th>
                <th>Pasture</th>
                <th class="sortable" @click="handleSortAcreage">
                  Acreage
                  <i class="sort-icon fa" :class="sortIcon(sortByAcreage)" />
                </th>
                <th>Uncertainty</th>
                <th>
                  Est. Co2e/Yr
                  <span class="th-info" title="Total possible CO2 Equivalent represent a per field per year estimate for the amount of GHG emission reduction due to practice(s) selected.">ⓘ</span>
                </th>
                <th class="sortable" @click="handleSortEstimatedCredit">
                  Estimated Credit
                  <i class="sort-icon fa" :class="sortIcon(sortByEstimatedCredit)" />
                </th>
                <th v-if="activeTab !== 'enteric'">Method</th>
                <th v-if="activeTab === 'enteric'">Year</th>
                <th>Date</th>
                <th>Model Status</th>
              </tr>
            </thead>

            <tbody>
              <SustainabilityRow
                v-for="field in paginatedRows"
                :key="field.fieldId"
                :field="field"
                :selectedType="activeTab"
                :isModalOpen="isModalOpen"
                :isToggleDaycentOn="isToggleDaycentOn"
                :nextCropSelection="nextCropSelections[field.fieldId]"
                @detail_field="getSimulationModal"
                @year_modal_enteric="openYearModal"
                @checked="checked"
                @unchecked="unchecked"
                ref="sustainabilityRows"
              />

              <!-- Empty state -->
              <tr v-if="paginatedRows.length === 0 && finishedLoading">
                <td colspan="11" class="empty-state">
                  Request Daycent Model Run to See Results
                </td>
              </tr>
              <tr v-if="paginatedRows.length === 0 && !finishedLoading">
                <td colspan="11" class="empty-state">
                  <span class="fetching-spinner"></span> Fetching Data…
                </td>
              </tr>
            </tbody>

            <!-- Totals footer -->
            <tfoot>
              <tr class="totals-row">
                <td></td>
                <td></td>
                <td></td>
                <td class="totals-label">Estimated Totals</td>
                <td class="totals-cell">{{ totalAcreage | prettyInteger }} Acres</td>
                <td></td>
                <td class="totals-cell">
                  <div class="co2-total">{{ totalCo2Equivalent | prettyInteger }}</div>
                  <div class="co2-sub">Total Tonnes CO2</div>
                </td>
                <td class="totals-cell text-center">
                  <span class="credit-chip credit-chip--green">
                    {{ totalCreditAmount | currency }}
                  </span>
                </td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="paginationLength > 1" class="pagination-bar">
          <span class="pagination-info">
            Showing {{ paginationIndices.first }}–{{ paginationIndices.last }}
          </span>
          <div class="pagination-controls">
            <button
              class="page-btn"
              :disabled="page === 1"
              @click="page--"
            >‹</button>
            <button
              v-for="p in paginationLength"
              :key="p"
              class="page-btn"
              :class="{ 'page-btn--active': p === page }"
              @click="page = p"
            >{{ p }}</button>
            <button
              class="page-btn"
              :disabled="page === paginationLength"
              @click="page++"
            >›</button>
          </div>
        </div>
      </div>

      <!-- ── Modals (unchanged logic) ───────────────────────────────────── -->
      <DaycentSimulationModal
        v-if="daycentSimulationModalOpen"
        :field="current_field"
        :is_complete="is_complete || false"
        @close-template-modal="closeDaycentSimulationModal"
        @success=""
      />

      <DaycentSimulationModal
        v-if="setAllCropFieldsModalOpen"
        :sustainabilityRowIds="updateAllRowIds"
        :selectedFieldIds="updateAllFieldIds"
        :selectedTypes="selectedType"
        :selectedTier="selectedTier"
        :selectedEntericYear="selectedEntericYear"
        :selectedTab="activeTab"
        @close-template-modal="closeSetAllCropFieldsModal"
        @processing="loadNewPastures"
        @success="success"
      />

      <SaveLoaderModal
        v-if="saveLoaderModalOpen"
        :loading="saveLoaderModalLoading"
        :loadComplete="saveLoaderModalLoadComplete"
        loadCompleteText="Saving Complete!"
        savingText="Saving Sustainability Settings"
        titleText="Saving Sustainability Settings"
        @close-template-modal="closeSaveLoaderModal"
      />

      <SaveLoaderModal
        v-if="reloadFieldsModalOpen"
        :loading="reloadFieldsModalLoading"
        :loadComplete="reloadFieldsModalLoadComplete"
        loadCompleteText="Loaded Sustainability Settings!"
        savingText="Loading Sustainability Settings"
        titleText="Loading Sustainability Settings"
        @close-template-modal="closeSaveLoaderModal"
      />

      <AdjustCarbonCreditModal
        v-if="adjustCarbonCreditModalOpen"
        :carbonCreditDollarValue="Number(carbonCreditDollarValue)"
        @update-carbon-credit="updateNewDollarPerTon"
        @close-template-modal="closeAdjustCarbonCreditModal"
      />

      <RunModelConfirmation
        v-if="runModelSetTypeModalOpen"
        :modelType="activeTab"
        @set-run-model-type="setAllCropFields"
        @close-template-modal="closeRunModelSetTypeModal"
      />

      <EntericMethaneYearModal
        v-if="isYearModalOpen"
        modalTitle="Pasture Details"
        :fieldData="selectedField"
        @close-modal="closeYearModal"
      />

      <!-- Download error modal -->
      <div v-if="isDownloadPdfValid" class="modal-overlay" @click.self="closeModal">
        <div class="error-modal">
          <button class="error-modal__close" @click="closeModal">✕</button>
          <p v-if="rowsToEdit.length < 1">No pasture selected. Please select one pasture.</p>
          <p v-else-if="rowsToEdit.length > 1">More than one pasture selected. Please select one pasture only.</p>
          <p v-else-if="downloadErrorMessage">{{ downloadErrorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from "vuex"
import CarbonReadyAPI from "@/api/CarbonReadyAPI"
import _ from "lodash"
import CarbonFlowSteppers from "@/views/CarbonFlowSteppers"
import SustainabilityRow from "@/components/sustainability/SustainabilityRow"
import { Filter, Fields, Dashboard, Pastures } from "@/store/modules"
import DaycentSimulationModal from "@/components/modals/DaycentSimulationModal"
import SaveLoaderModal from "@/components/modals/SaveLoaderModal"
import AdjustCarbonCreditModal from "@/components/modals/AdjustCarbonCreditModal"
import SearchBar from "@/components/misc/SearchBar"
import Permissions from "@/components/permissions/Permissions"
import { IS_RANCHFORCE } from "@/constants"
import PasturesAPI from "../api/PasturesAPI"
import RunModelConfirmation from "@/components/modals/RunModelConfirmation"
import EntericMethaneYearModal from "@/components/modals/EntericMethaneYearModal.vue";

export default {
  name: "Sustainability",

  components: {
    CarbonFlowSteppers,
    SustainabilityRow,
    DaycentSimulationModal,
    SaveLoaderModal,
    AdjustCarbonCreditModal,
    SearchBar,
    Permissions,
    RunModelConfirmation,
    EntericMethaneYearModal,
  },

  data() {
    return {
      activeTab: "enteric",
      isModalOpen: false,
      rowsToEdit: [],
      isDownloadPdfValid: false,
      checkedFields: [],
      is_complete: false,
      selectedFieldIds: [],
      daycentSimulationModalOpen: false,
      setAllCropFieldsModalOpen: false,
      saveLoaderModalOpen: false,
      saveLoaderModalLoading: false,
      saveLoaderModalLoadComplete: false,
      reloadFieldsModalOpen: false,
      reloadFieldsModalLoading: false,
      reloadFieldsModalLoadComplete: false,
      current_field: null,
      all_fields: null,
      searchString: "",
      finishedLoading: false,
      sortByAcreage: null,
      sortByEstimatedCredit: null,
      showSaveBtn: false,
      showSaveBtnLoader: null,
      updateAllRowIds: [],
      updateAllFieldIds: [],
      dataCopy: null,
      page: 1,
      paginationSize: 100,
      adjustCarbonCreditModalOpen: false,
      loading: false,
      tableKey: 1,
      isRanchForce: IS_RANCHFORCE,
      dateFilterString: "",
      isToggleDaycentOn: false,
      downloadErrorMessageModel: false,
      downloadErrorMessage: "",
      runModelSetTypeModalOpen: false,
      selectedType: "",
      selectedTier: "",
      selectedEntericYear: null,
      updateAllFieldTypes: [],
      isYearModalOpen: false,
      selectedField: null,
    }
  },

  computed: {
    ...mapGetters({
      activeFields: Filter.Getters.getSelectedFields,
      activeCrops: Filter.Getters.getDashboardCrops,
    }),
    ...mapState({
      isReadOnly: state => state.User.user.permission === "Read-Only",
      selectedFields: state => state.Filter.selectedFields,
      cropDesign: state => state.Dashboard.cropDesign,
      nextCropSelections: state => state.Dashboard.nextCropSelections,
      carbonCreditDollarValue: state => state.Dashboard.carbonCreditDollarValue,
      userSustainabilityBeforeRefresh: state => state.Fields.userSustainabilityBeforeRefresh,
      showSustainability: state => state.Organization.organization.showSustainability,
    }),

    carbonCreditDollarValueLabel() {
      if (this.carbonCreditDollarValue)
        return "Carbon Price: $" + this.carbonCreditDollarValue + "/Tonne"
      return "$$/Tonne"
    },

    paginatedRows() {
      if (this.searchString) return this.searchFilteredItems
      const firstIndex = (this.page - 1) * this.paginationSize
      const maxIndex = this.paginationSize * this.page
      return this.fieldRows.slice(firstIndex, maxIndex)
    },

    paginationLength() {
      return Math.ceil(this.fieldRows.length / this.paginationSize)
    },

    paginationIndices() {
      const firstIndex = (this.page - 1) * this.paginationSize + 1
      const maxIndex = this.paginationSize * this.page
      const lastIndex = this.fieldRows.length < maxIndex ? this.fieldRows.length : maxIndex
      return { first: firstIndex, last: lastIndex }
    },

    searchFilteredItems() {
      if (!this.searchString) return this.fieldRows
      const s = this.searchString.toLocaleLowerCase()
      return this.fieldRows.filter(f => f.name.toLowerCase().includes(s))
    },

    fieldRows() {
      const sortValues = {
        acreage: this.sortByAcreage,
        creditAmount: this.sortByEstimatedCredit,
      }
      let sortColumns = []
      let sortDirections = []
      
      for (const col in sortValues) {
        if (sortValues[col] !== null) {
          sortColumns.push(col)
          sortDirections.push(sortValues[col])
        }
      }

      let rows = []
      this.activeFields.forEach(field => {
        let selectedDaycent;
        switch (this.activeTab) {
          case 'daycent':
              selectedDaycent = field?.daycent?.find(
                d => d.is_mems_output === this.isToggleDaycentOn
              )
            break;

          case 'mems':
            selectedDaycent = field?.daycent?.find(
              d => d.is_mems_output === this.isToggleDaycentOn
            )
            break;

          case 'enteric':
            const completedDaycents = field?.enteric_methane.filter(v => v.status === "complete");
            const defaultDaycent = field?.enteric_methane.find(v => v.tier === "ipcc_tier1") ?? field?.enteric_methane.find(v => v.tier === "ipcc_tier2");

            selectedDaycent = completedDaycents.find(v => v.tier === "ipcc_tier1")
              ?? completedDaycents[0] 
              ?? defaultDaycent;
            break;

          default:
            break;
        }

        // const selectedDaycent = field?.daycent?.find(
        //   d => d.is_mems_output === this.isToggleDaycentOn
        // )
        if (selectedDaycent) {
          const d = selectedDaycent
          const equiv = this.activeTab === 'enteric' ? d.total_methane_emission_co2e :
            (parseFloat(d.sysc) * 3.666 * 4046.86 +
              parseFloat(d.ch4) * -1 * 24 * 4046.86 +
              parseFloat(d.n20) * -1 * 298 * 4046.86) /
            1000000

          let any_selected = false
          let no_till = this.getPracticeSetting(d, "no-till")
          let cover_crop = this.getPracticeSetting(d, "cover-crop")
          let inhibitor = this.getPracticeSetting(d, "nitrogen-inhibitor")
          let awd = this.getPracticeSetting(d, "awd")
          let scope3_ch4 = this.getPracticeSetting(d, "scope3_ch4")

          if (inhibitor.is_selected) any_selected = true
          if (cover_crop.is_selected) any_selected = true
          if (no_till.is_selected) any_selected = true
          if (awd.is_selected) any_selected = true
          if (!scope3_ch4.is_selected) any_selected = true

          let creditAmt = 0
          let co2EquivalentSelected = 0
          if (any_selected) {
            creditAmt = this.activeTab === 'enteric' ? d.total_methane_emission_co2e * this.carbonCreditDollarValue : equiv.toFixed(2) * this.carbonCreditDollarValue * field.acreage.toFixed(0)
            co2EquivalentSelected = this.activeTab === 'enteric' ? d.total_methane_emission_co2e : creditAmt / this.carbonCreditDollarValue
          }

          rows.push({
            fieldId: field.id,
            name: field.name,
            acreage: field.acreage,
            uncertainty: co2EquivalentSelected * 0.2,
            boundary_image: field.boundary_image,
            co2Equivalent: equiv,
            status: d.status,
            is_complete: false,
            method: this.activeTab === 'enteric' ? d.tier : d.method,
            year: d?.year,
            date: d.updated_at ? new Date(d.updated_at).toISOString().split("T")[0] : null,
            co2EquivalentSelected,
            creditAmount: creditAmt,
          })
        } else {
          rows.push({
            fieldId: field.id,
            name: field.name,
            acreage: field.acreage,
            uncertainty: 0,
            boundary_image: field.boundary_image,
            status: "",
            is_complete: false,
            method: "",
            date: null,
            co2EquivalentSelected: 0,
            creditAmount: 0,
          })
        }
      })

      return _.orderBy(rows, sortColumns, sortDirections)
    },

    totalAcreage() {
      return this.fieldRows.map(r => r.acreage).reduce((p, c) => p + c, 0)
    },
    totalCo2Equivalent() {
      return this.fieldRows
        .map(r => {
          let v = Number(r.co2EquivalentSelected)
          if (v <= 0) v = v * -1
          if (isNaN(v)) v = 0
          return v
        })
        .reduce((p, c) => p + c, 0)
    },
    totalCreditAmount() {
      return this.fieldRows
        .map(r => (parseFloat(r.creditAmount) ? Math.abs(parseFloat(r.creditAmount)) : 0))
        .reduce((prev, curr) => (curr < 0 ? prev : prev + curr), 0)
    },
    checkedCount() {
      return this.rowsToEdit.length
    },
  },

  watch: {
    all_fields() {
      this.finishedLoading = true
    },
    fieldRows(newRows) {
      const arrayMapToCheck = newRows.map(
        f => `${f?.awd?.is_selected} ${f?.cover_crop?.is_selected} ${f?.inhibitor?.is_selected} ${f?.no_till?.is_selected}`
      )
      this.showSaveBtn = !(arrayMapToCheck.join("") === this.dataCopy.join(""))
    },
    selectedFields() {
      this.dataCopy = this.fieldRows.map(
        f => `${f?.awd?.is_selected} ${f?.cover_crop?.is_selected} ${f?.inhibitor?.is_selected} ${f?.no_till?.is_selected}`
      )
    },
  },

  methods: {
    ...mapActions({
      fetchFields: Fields.Actions.fetchFieldsSkinny,
      updateRegenPractice: Fields.Actions.updateRegenPractice,
      updateUserSustainability: Fields.Actions.updateUserSustainability,
      clearUserSustainability: Fields.Actions.clearUserSustainability,
      updateCarbonCreditDollarValue: Dashboard.Actions.updateCarbonCreditDollarValue,
      fetchPastureFields: Pastures.Actions.fetchFields,
      fetchEntericMethaneCalcData: Fields.Actions.fetchEntericMethaneCalcData,
    }),
    ...mapMutations({
      setSelectedFields: Filter.Mutations.setSelectedFields,
      setCarbonCreditDollarValue: Dashboard.Mutations.setCarbonCreditDollarValue,
    }),

    updateNewDollarPerTon(value) {
      this.loading = true
      this.updateCarbonCreditDollarValue(value)
      this.closeAdjustCarbonCreditModal()
      this.loading = false
      this.tableKey += 1
    },

    getPracticeSetting(d, practice_name) {
      let ps = d?.scenario?.filter(x => x.regen_practice.name === practice_name)
      if (ps?.length === 1) {
        return { value: ps[0].value, is_selected: Boolean(ps[0].is_selected), is_locked: Boolean(ps[0].is_locked) }
      }
      return { value: "Unavailable", is_selected: false }
    },

    getSimulationModal(d, is_complete) {
      this.daycentSimulationModalOpen = true
      this.is_complete = is_complete
      this.current_field = d
    },
    closeDaycentSimulationModal() { this.daycentSimulationModalOpen = false },
    closeSetAllCropFieldsModal() { this.selectedType = ""; this.setAllCropFieldsModalOpen = false },
    closeSaveLoaderModal() { this.saveLoaderModalOpen = false },

    success() {
      if (this.current_field) {
        this.current_field.status = "pending"
      } else {
        this.fieldRows.forEach(row => {
          if (this.rowsToEdit.includes(row.id)) row.status = "pending"
        })
      }
      this.setAllCropFieldsModalOpen = false
      this.daycentSimulationModalOpen = false
      this.selectedType = ""
      this.current_field = null
    },

    setAllCropFields(data) {
      this.updateAllRowIds = []
      this.updateAllFieldIds = []
      const selectedRows = this.fieldRows.filter(row => this.rowsToEdit.includes(row.fieldId))
      selectedRows.forEach(row => {
        if (data?.dayCentModelType?.name === "Pasture wise") row.RUN_MODEL_PASTURE_WISE = data?.dayCentModelType?.value
        if (data?.dayCentModelType?.name === "Paddock wise") row.RUN_MODEL_PADDOCK_WISE = data?.dayCentModelType?.value
        this.updateAllRowIds.push(row.id)
        this.updateAllFieldIds.push(row.fieldId)
      })
      this.selectedType = data?.dayCentModelType
      this.selectedTier = data?.entericModelType
      this.selectedEntericYear = data?.year
      this.runModelSetTypeModalOpen = false
      this.setAllCropFieldsModalOpen = true
    },

    selectAllCheckBox() {
      this.isModalOpen = true
      const checked = this.$refs.fieldSelectAllCheckbox.checked
      this.$refs.sustainabilityRows.forEach(r => r.setFieldSelectCheckbox(checked))
      if (!checked) { this.rowsToEdit = []; this.selectedFieldIds = []; this.isModalOpen = false }
    },

    checked(id, fieldId) {
      if (!this.rowsToEdit.includes(fieldId)) {
        this.rowsToEdit = [...this.rowsToEdit, fieldId]
        this.selectedFieldIds = [...this.selectedFieldIds, fieldId]
      }
      this.checkedFields.push(fieldId)
      this.isModalOpen = this.rowsToEdit.length > 0
      if (this.rowsToEdit.length === this.fieldRows.length) {
        this.$refs.fieldSelectAllCheckbox.checked = true
      }
    },

    unchecked(id, fieldId) {
      this.rowsToEdit = this.rowsToEdit.filter(i => i !== fieldId)
      this.selectedFieldIds = this.selectedFieldIds.filter(i => i !== fieldId)
      this.isModalOpen = this.rowsToEdit.length > 0
      if (this.rowsToEdit.length !== this.fieldRows.length) {
        this.$refs.fieldSelectAllCheckbox.checked = false
      }
      if (this.checkedFields.length > 0) {
        this.checkedFields = this.checkedFields.filter(e => e !== fieldId)
      }
    },

    async loadPastures() {
      try { await this.fetchPastureFields() } catch (err) { console.error(err) }
    },
    async loadNewPastures() {
      setTimeout(async () => {
        try { await this.fetchPastureFields() } catch (err) { console.error(err) }
      }, 1000);
    },

    handleAdjustCarbonCreditModalClick() { this.adjustCarbonCreditModalOpen = true },
    closeAdjustCarbonCreditModal() { this.adjustCarbonCreditModalOpen = false },

    async downloadFieldSelection() {
      if (this.rowsToEdit.length !== 1) { this.isDownloadPdfValid = true; return }
      await CarbonReadyAPI.fieldSelectionPdf({
        is_mems_output: this.isToggleDaycentOn,
        pasture_id: this.checkedFields[0],
        carbon_price: Number(this.carbonCreditDollarValue),
      }).then(response => {
        if (response.data.data === false) {
          this.isDownloadPdfValid = true
          this.downloadErrorMessage = response.data.message
          return
        }
        const { file_name, file_data } = response.data
        const byteCharacters = atob(file_data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i)
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = file_name
        link.click()
        URL.revokeObjectURL(link.href)
      })
    },

    sortIcon(sortValue) {
      if (sortValue === "asc") return "fa-sort-up"
      else if (sortValue === "desc") return "fa-sort-down"
      return "fa-sort"
    },
    handleSortAcreage() {
      if (!this.sortByAcreage) this.sortByAcreage = "asc"
      else if (this.sortByAcreage === "asc") this.sortByAcreage = "desc"
      else this.sortByAcreage = null
    },
    handleSortEstimatedCredit() {
      if (!this.sortByEstimatedCredit) this.sortByEstimatedCredit = "asc"
      else if (this.sortByEstimatedCredit === "asc") this.sortByEstimatedCredit = "desc"
      else this.sortByEstimatedCredit = null
    },

    saveBeforeRefresh() {
      const dict = {}
      this.activeFields.forEach(field => {
        let daycent = field?.daycent[0]
        dict[field.id] = { scenario: _.cloneDeep(daycent?.scenario) }
      })
      this.updateUserSustainability(dict)
    },
    loadAfterRefresh() {
      if (_.isEmpty(this.userSustainabilityBeforeRefresh)) return
      for (const [key, value] of Object.entries(this.userSustainabilityBeforeRefresh)) {
        this.updateRegenPractice({ field_id: key, scenario: value.scenario })
      }
      this.clearUserSustainability()
    },
    async intervalFunc() {
      this.saveBeforeRefresh()
      await this.fetchFields()
      this.loadAfterRefresh()
    },
    closeModal() { this.isDownloadPdfValid = false; this.downloadErrorMessage = "" },
    closeRunModelSetTypeModal() {
      this.runModelSetTypeModalOpen = false
    },

    async openYearModal (id) {
      try {
        const data = await this.fetchEntericMethaneCalcData({
          id
        });

        this.selectedField = data.data
        this.isYearModalOpen = true
      } catch (error) {
        console.log("Failed to fetch data ---", error);
      }
    },

    closeYearModal() {
      this.selectedField = null
      this.isYearModalOpen = false
    }
  },

  mounted() {
    this.all_fields = setInterval(this.intervalFunc, 120000)
    this.dataCopy = this.fieldRows.map(
      f => `${f?.awd?.is_selected} ${f?.cover_crop?.is_selected} ${f?.inhibitor?.is_selected} ${f?.no_till?.is_selected}`
    )
  },
  destroyed() {
    if (this.all_fields) clearInterval(this.all_fields)
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.sustainability-page {
  font-family: 'DM Sans', sans-serif;
  min-height: 100vh;
  color: #1a1d23;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 0 18px;
  flex-wrap: wrap;
  justify-content: space-between
}

.page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
}

.page-title h2 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1d23;
  letter-spacing: -0.2px;
  white-space: nowrap;
}

.globe-icon {
  font-size: 18px;
}

.tab-switcher {
  display: flex;
  background: #fff;
  border: 1px solid #e2e6eb;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.tab-btn {
  padding: 8px 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.tab-btn:hover:not(.active) {
  background: #f5f6f8;
  color: #374151;
}

.tab-btn.active {
  background: #4caf50;
  color: #fff;
  font-weight: 600;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: #374151;
  background: #fff;
  border: 1px solid #e2e6eb;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.action-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.action-btn--dark {
  background: #1a1d23;
  color: #fff;
  border-color: #1a1d23;
}

.action-btn--dark:hover {
  background: #2d3139;
  border-color: #2d3139;
}

.carbon-price-btn .price-value {
  color: #4caf50;
  font-weight: 600;
}

.info-icon {
  color: #9ca3af;
  font-size: 13px;
}

.run-model-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  color: #374151;
  background: #fff;
  border: 1px solid #e2e6eb;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.run-model-btn:hover { background: #f3f4f6; }
.run-model-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.table-container {
  background: #fff;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}

.data-table thead tr {
  background: linear-gradient(90deg, #E6E9EE 0%, #DDEDD1 100%);
}

.data-table thead th {
  padding: 12px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #2f3b2f;
  text-align: left;
  border-right: 1px solid rgba(255,255,255,0.4);
}

.data-table thead th.sortable {
  cursor: pointer;
  user-select: none;
}

.data-table thead th.sortable:hover { color: #374151; }

.sort-icon {
  margin-left: 4px;
  font-size: 11px;
  color: #9ca3af;
}

.th-info {
  margin-left: 4px;
  font-size: 11px;
  color: #9ca3af;
  cursor: help;
}

.data-table tbody tr {
  border-bottom: 1px solid #f0f2f5;
  transition: background 0.12s;
}

.data-table tbody tr:hover { background: #fafbfc; }
.data-table tbody tr:last-child { border-bottom: none; }

.data-table tbody tr td {
  padding: 14px 20px;
  color: #374151;
  vertical-align: middle;
}

.col-check { width: 40px; }
.col-action { width: 130px; }

.custom-checkbox {
  width: 15px;
  height: 15px;
  accent-color: #4caf50;
  cursor: pointer;
}

.empty-state {
  text-align: center;
  padding: 48px 20px !important;
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
}

.fetching-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #d1d5db;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin { to { transform: rotate(360deg); } }

tfoot .totals-row {
  background: #edf7ee;
  border-top: 2px solid #c3e6c5;
}

tfoot .totals-row td {
  padding: 14px 14px;
}

.totals-label {
  font-weight: 700;
  font-size: 14px;
  color: #1a1d23;
  white-space: nowrap;
}

.totals-cell {
  font-weight: 600;
  color: #1a1d23;
  white-space: nowrap;
  text-align: center;
}

.co2-total {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d23;
}

.co2-sub {
  font-size: 11.5px;
  font-weight: 400;
  color: #6b7280;
  margin-top: 2px;
}

.credit-chip {
  display: inline-block;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: 'DM Mono', monospace;
  white-space: nowrap;
}

.credit-chip--green {
  background: #4caf50;
  color: #fff;
}

.credit-chip--red {
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
}

.credit-chip--zero {
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 0 4px;
}

.pagination-info {
  font-size: 13px;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #fff;
  border: 1px solid #e2e6eb;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.page-btn:hover:not(.page-btn--active):not(:disabled) {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.page-btn--active {
  background: #4caf50;
  color: #fff;
  border-color: #4caf50;
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}

.error-modal {
  position: relative;
  background: #fff;
  padding: 36px 40px;
  border-radius: 16px;
  width: 480px;
  max-width: 90vw;
  text-align: center;
  box-shadow: 0 8px 40px rgba(0,0,0,0.14);
}

.error-modal p {
  font-size: 15px;
  font-weight: 500;
  color: #374151;
  line-height: 1.6;
}

.error-modal__close {
  position: absolute;
  top: 14px;
  right: 16px;
  background: none;
  border: none;
  font-size: 16px;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s;
}

.error-modal__close:hover { color: #374151; }
</style>